/* The Bean Archives — product page interactions. Loaded only on the product template.
   Gallery thumbnail swap, quantity steppers, and variant selection (price /
   availability / image / hidden id update). Progressive enhancement. */
(() => {
  "use strict";
  const root = document.querySelector(".pdp");
  if (!root) return;

  const main = root.querySelector("[data-pdp-main] img");

  /* ---- Gallery thumbnails ---- */
  const thumbs = root.querySelectorAll(".pdp__thumb");
  thumbs.forEach((t) =>
    t.addEventListener("click", () => {
      const img = t.querySelector("img");
      if (main && img) { main.src = img.dataset.full || img.src; main.alt = img.alt; }
      thumbs.forEach((x) => x.classList.toggle("is-active", x === t));
    })
  );

  /* ---- Quantity steppers ---- */
  const qty = root.querySelector("[data-pdp-qty]");
  /* Per-order cap from the product metafield (data-pdp-max); Infinity when unset. */
  const maxQty = qty && +qty.dataset.pdpMax > 0 ? +qty.dataset.pdpMax : Infinity;
  const setQty = (n) => { if (qty) qty.value = Math.min(maxQty, Math.max(1, n)); };
  qty?.addEventListener("change", () => setQty(+qty.value || 1));
  root.querySelector("[data-pdp-dec]")?.addEventListener("click", () => setQty((+qty.value || 1) - 1));
  root.querySelector("[data-pdp-inc]")?.addEventListener("click", () => setQty((+qty.value || 1) + 1));

  /* ---- Add to cart (AJAX — stay on the page; no-JS falls back to a normal POST) ---- */
  const form = root.querySelector("form");
  const addBtn = root.querySelector("[data-pdp-add]");

  async function refreshCartCount() {
    try {
      const cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
      const link = document.querySelector(".nav__cart");
      if (!link) return;
      let badge = link.querySelector(".nav__cart-count");
      if (cart.item_count > 0) {
        if (!badge) { badge = document.createElement("span"); badge.className = "nav__cart-count"; link.appendChild(badge); }
        badge.textContent = cart.item_count;
      } else if (badge) { badge.remove(); }
      link.setAttribute("aria-label", "Cart (" + cart.item_count + ")");
    } catch (e) { /* non-fatal */ }
  }

  if (form && addBtn) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;
      const label = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = "Adding…";
      try {
        /* Cap counts what is already in the cart for this variant, so two adds can't stack past it. */
        if (maxQty !== Infinity && qty) {
          const cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
          const vid = +(new FormData(form).get("id") || 0);
          const inCart = cart.items.filter((i) => i.variant_id === vid).reduce((n, i) => n + i.quantity, 0);
          const room = maxQty - inCart;
          if (room <= 0) {
            addBtn.textContent = "Limit " + maxQty + " per order";
            setTimeout(() => { addBtn.textContent = label; addBtn.disabled = false; }, 2200);
            return;
          }
          if ((+qty.value || 1) > room) setQty(room);
        }
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        if (!res.ok) throw new Error("add failed");
        addBtn.textContent = "Added ✓";
        await refreshCartCount();
        setTimeout(() => { addBtn.textContent = label; addBtn.disabled = false; }, 1800);
      } catch (err) {
        addBtn.textContent = "Try again";
        setTimeout(() => { addBtn.textContent = label; addBtn.disabled = false; }, 1800);
      }
    });
  }

  /* ---- Variant selection ---- */
  const dataEl = root.querySelector("[data-pdp-json]");
  const variantWrap = root.querySelector("[data-pdp-variants]");
  if (!dataEl || !variantWrap) return;

  let variants = [];
  try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

  const idSel = root.querySelector("[data-pdp-id]");
  const priceNow = root.querySelector(".pdp__now");
  const priceWas = root.querySelector(".pdp__was");
  const money = (cents) => "$" + (cents / 100).toFixed(2).replace(/\.00$/, "");

  /* ---- Two-level picker (coffee → amount) --------------------------------
     When the product has two options we render coffee cards (option1) + amount
     pills (option2). Whenever the coffee changes, re-point every amount pill at
     that coffee's variant: fill its price, disable amounts that don't exist or
     are sold out, and keep a valid amount selected. Falls through harmlessly
     for the generic single-option picker. */
  const twoLevel = variantWrap.hasAttribute("data-two-level");
  const selectedCoffee = () => root.querySelector('input[name="option1"]:checked')?.value ?? null;

  const syncAmounts = () => {
    if (!twoLevel) return;
    const coffee = selectedCoffee();
    const pills = root.querySelectorAll(".amountpill");
    let checkedOk = false;
    pills.forEach((pill) => {
      const amount = pill.querySelector("input").value;
      const v = variants.find((vt) => vt.options[0] === coffee && vt.options[1] === amount);
      const input = pill.querySelector("input");
      const priceEl = pill.querySelector("[data-amount-price]");
      const wasEl = pill.querySelector("[data-amount-was]");
      const avail = !!(v && v.available);
      pill.classList.toggle("is-soldout", !!v && !v.available);
      pill.classList.toggle("is-missing", !v);
      input.disabled = !avail;
      if (priceEl) priceEl.textContent = v ? money(v.price) : "—";
      // struck compare-at, only when this size actually has one (e.g. the Set)
      if (wasEl) wasEl.textContent = v && v.compare_at_price && v.compare_at_price > v.price ? money(v.compare_at_price) : "";
      if (input.checked && !avail) input.checked = false;
      else if (input.checked && avail) checkedOk = true;
    });
    if (!checkedOk) {
      const firstOk = [...pills].find((p) => !p.querySelector("input").disabled);
      if (firstOk) firstOk.querySelector("input").checked = true;
    }
  };

  // `fromUser` distinguishes the initial paint from a real selection. Passed explicitly
  // rather than tracked in a flag, so an early return on load can't leave the flag set
  // and swallow the first real swap.
  const pick = (fromUser) => {
    const chosen = [];
    root.querySelectorAll(".pdp__opt input:checked").forEach((i) => chosen.push(i.value));
    const key = chosen.join(" / ");
    const v = variants.find((vt) => vt.options.join(" / ") === key);
    if (!v) return;

    if (idSel) idSel.value = v.id;
    if (priceNow) priceNow.textContent = money(v.price);
    if (priceWas) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        priceWas.textContent = money(v.compare_at_price);
        priceWas.style.display = "";
      } else {
        priceWas.style.display = "none";
      }
    }
    if (addBtn) {
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? "Add to cart" : "Sold out";
    }
    // Swap the hero to the variant's own photo only once the shopper actually picks
    // something. On first paint we leave whatever the gallery rendered, so the page
    // opens on the product's first image rather than the first variant's.
    if (fromUser && v.featured_image && main) {
      main.src = v.featured_image.src;
      main.alt = v.featured_image.alt || main.alt;
    }
  };

  // "change" covers keyboard arrows and picking a different value; "click" additionally
  // covers re-clicking the value that is ALREADY selected, which fires no change event —
  // without it, clicking the coffee the page opened on would never swap the hero to its
  // photo. Calling pick twice on a fresh selection is harmless, it just re-renders.
  const onCoffee = () => { syncAmounts(); pick(true); };
  variantWrap.querySelectorAll('input[name="option1"]').forEach((r) => {
    r.addEventListener("change", onCoffee);
    r.addEventListener("click", onCoffee);
  });
  variantWrap.querySelectorAll('input[name="option2"]').forEach((r) =>
    r.addEventListener("change", () => pick(true)));
  // generic single-option picker: any remaining radios just re-pick
  variantWrap.querySelectorAll('input[type="radio"]:not([name="option1"]):not([name="option2"])').forEach((r) => {
    r.addEventListener("change", () => pick(true));
    r.addEventListener("click", () => pick(true));
  });

  syncAmounts();
  pick(false);
})();
